import type { ThemePreference } from '@deskworks/domain';
import { useEffect, useState } from 'react';

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

export function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'system') return media?.matches ? 'dark' : 'light';
  return pref;
}

/** Applies Light / Dark / Follow System and keeps following the OS while set to System. */
export function applyTheme(pref: ThemePreference) {
  try {
    localStorage.setItem('dw-theme', pref);
  } catch {
    // Storage may be unavailable (private browsing); the setting still applies for this tab.
  }
  document.documentElement.setAttribute('data-theme', resolveTheme(pref));
}

export function useThemeSync(pref: ThemePreference | undefined) {
  useEffect(() => {
    if (!pref) return;
    applyTheme(pref);
    if (pref !== 'system' || !media) return;
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [pref]);
}

export function useResolvedTheme(): 'light' | 'dark' {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => (document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'));
  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

export function useMediaQuery(query: string) {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    m.addEventListener('change', on);
    on();
    return () => m.removeEventListener('change', on);
  }, [query]);
  return match;
}

/** Layout breakpoints from the design notes: >1100 three-pane, 760–1100 two-pane, <760 single pane. */
export function useLayout(): 'wide' | 'medium' | 'narrow' {
  const wide = useMediaQuery('(min-width: 1101px)');
  const narrow = useMediaQuery('(max-width: 759px)');
  return wide ? 'wide' : narrow ? 'narrow' : 'medium';
}
